import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import { ToolTip } from 'components/Tooltip/ToolTip';
import moment from 'moment';
import React, { FC, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Scrollbar } from 'react-scrollbars-custom';
import { futureNoOfDaysList } from 'utils/constants/data';
import { routes } from 'utils/constants/routes';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';
import { getColor, getLabel, ScrollTop } from 'utils/validators/HelperFunctions';

interface UpComingBookingProps {
  upComingBooking: any;
  title: string;
  noOfDays: string;
  setNoOfDays: any;
  isExportable?: boolean;
  handleExport?: () => void;
  maxBodyHeight?: number;
}
const UpComingBooking: FC<UpComingBookingProps> = ({
  upComingBooking,
  title,
  noOfDays,
  setNoOfDays,
  isExportable,
  handleExport,
  maxBodyHeight = 280,
}) => {
  const futureNoOfDaysOptions = futureNoOfDaysList(strings);
  const theme = useTheme();

  const iconStyle = {
    top: 2,
    fontSize: '14px',
    position: 'relative',
    marginRight: 1,
  };

  const handleChange = (e: any) => {
    setNoOfDays(e.target.value);
  };

  const formatINR = (value: number) =>
    formatCurrencyWithSymbol(value);

  const urgencyChip = (startDate: number) => {
    const today = moment().startOf('day');
    const start = moment(startDate).startOf('day');
    const diff = start.diff(today, 'days');

    if (diff < 0) return null;
    if (diff === 0) return { label: 'TODAY', bg: '#FEE2E2', fg: '#991B1B' };
    if (diff === 1) return { label: 'TOMORROW', bg: '#FFEDD5', fg: '#9A3412' };
    if (diff <= 3) return { label: `IN ${diff} DAYS`, bg: '#FEF9C3', fg: '#854D0E' };
    return null;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Ignore copy failures (permissions / http)
    }
  };

  const isPotential = () => {
    const t = String(title || '').toLowerCase();
    return t.includes('tentative') || t.includes('waitlisted');
  };

  /** Indian numbering threshold: above one lakh. */
  const HIGH_VALUE_INR = 100_000;
  const isHighValueRevenue = (revenue: unknown) => typeof revenue === 'number' && revenue > HIGH_VALUE_INR;

  const followUpBadgeLabel = (item: any): string | null => {
    if (!isPotential()) return null;
    const createdMs = item?.createdAt;
    if (createdMs == null || Number.isNaN(Number(createdMs))) return null;
    const daysOld = Math.max(0, moment().startOf('day').diff(moment(Number(createdMs)).startOf('day'), 'days'));
    if (daysOld < 2) return null;
    const st = String(item?.status ?? '').toUpperCase();
    const statusLabel = st === 'WAITLISTED' ? 'WAITLISTED' : st === 'TENTATIVE' ? 'TENTATIVE' : st || 'BOOKING';
    const dayWord = daysOld === 1 ? 'DAY' : 'DAYS';
    return `${statusLabel} · ${daysOld} ${dayWord} OLD`;
  };

  const summary = (() => {
    const count = Array.isArray(upComingBooking) ? upComingBooking.length : 0;
    const total = Array.isArray(upComingBooking)
      ? upComingBooking.reduce((acc: number, item: any) => acc + (typeof item?.revenue === 'number' ? item.revenue : 0), 0)
      : 0;

    const noun = count === 1 ? 'Booking' : 'Bookings';
    const label = isPotential() ? 'potential' : 'revenue';
    return `${count} ${noun} · ${formatINR(total)} ${label}`;
  })();

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Grid container alignItems={'center'} justifyContent={'space-between'} px={0} gap={1}>
          <Grid item xs={12} sm={6} container gap={1} alignItems="center">
            <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
              {title}
            </Typography>
            {isExportable && (
              <ToolTip title={strings.exportBod}>
                <DownloadIcon sx={{ cursor: 'pointer', color: theme.palette.text.primary }} onClick={handleExport} />
              </ToolTip>
            )}
          </Grid>
          <Grid item xs={10} sm={5}>
            <FormControl
              sx={{
                width: '100%',
                '& .MuiSelect-select': { height: '25px', lineHeight: '25px' },
              }}
              size="small">
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={noOfDays}
                onChange={handleChange}>
                {futureNoOfDaysOptions.map((item: any) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <CardContent sx={{ p: 0, pb: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>{summary}</Typography>
        </Box>
        <Scrollbar style={{ width: '100%', height: maxBodyHeight, fontFamily: theme.typography.fontFamily }}>
          <List
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              pt: 0,
              px: 2,
              pb: 2,
            }}>
            {upComingBooking?.length > 0 ? (
              upComingBooking.map((upcoming: any) => {
                const highValue = isHighValueRevenue(upcoming?.revenue);
                const followUpLabel = followUpBadgeLabel(upcoming);
                return (
                <Fragment key={upcoming.lineItemId}>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      mb: 1,
                      border: highValue
                        ? `1px solid rgba(202, 138, 4, 0.45)`
                        : `1px solid ${theme.palette.divider}`,
                      borderLeft: highValue ? '4px solid #CA8A04' : undefined,
                      background: highValue
                        ? 'linear-gradient(90deg, rgba(254, 243, 199, 0.42) 0%, #fafafa 45%)'
                        : '#fafafa',
                      boxShadow: highValue ? '0 1px 10px rgba(202, 138, 4, 0.12)' : 'none',
                    }}>
                    <ListItem alignItems="flex-start" sx={{ p: 0 }}>
                    <ListItemText
                      primary={
                        <Grid container>
                          <Grid item container xs={6} sm={9} alignItems={'center'}>
                            {upcoming.title && (
                              <Grid item>
                                <Typography
                                  variant="subtitle2"
                                  sx={
                                    highValue
                                      ? { fontWeight: 800, color: 'text.primary', fontSize: 12.5 }
                                      : { fontSize: 12.5 }
                                  }>
                                  {upcoming.title} {'   '}
                                </Typography>
                              </Grid>
                            )}
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                ml: { md: 2 },
                              }}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  borderRadius: 1,
                                  px: 0.75,
                                  py: 0.25,
                                  ml: -0.75,
                                  transition: 'background-color 160ms ease',
                                  '&:hover': { backgroundColor: '#eef2ff' },
                                }}>
                                <Link
                                  onClick={() => ScrollTop(0)}
                                  to={`${routes.bookings}/${upcoming.lineItemId}`}
                                  style={{ textDecoration: 'none' }}>
                                  <Typography
                                    fontWeight={800}
                                    sx={{
                                      color: theme.palette.success.main,
                                      fontSize: 11,
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                    variant="caption">
                                    {upcoming.orderReference}
                                  </Typography>
                                </Link>
                                {upcoming.orderReference ? (
                                  <Tooltip title="Copy booking reference" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e: any) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        copyToClipboard(String(upcoming.orderReference));
                                      }}
                                      sx={{
                                        ml: 0.25,
                                        p: 0.25,
                                        color: 'text.secondary',
                                        '&:hover': { color: 'text.primary' },
                                      }}>
                                      <ContentCopyRoundedIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </Box>
                              {typeof upcoming.revenue === 'number' ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: highValue ? 900 : 700,
                                    color: highValue ? '#854D0E' : 'text.primary',
                                    fontSize: 11,
                                    letterSpacing: highValue ? 0.02 : undefined,
                                  }}>
                                  · {formatINR(upcoming.revenue)}
                                </Typography>
                              ) : null}
                              {typeof upcoming.guests === 'number' && upcoming.guests > 0 ? (
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 700, color: 'text.primary', fontSize: 11 }}>
                                  · {upcoming.guests} pax
                                </Typography>
                              ) : null}
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3} display={'flex'} justifyContent={'flex-end'}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {followUpLabel ? (
                                <Tooltip title="Follow-up needed" arrow>
                                  <Chip
                                    size="small"
                                    label={followUpLabel}
                                    sx={{
                                      maxWidth: '100%',
                                      height: 'auto',
                                      py: 0.25,
                                      fontWeight: 800,
                                      fontSize: 9,
                                      letterSpacing: 0.3,
                                      border: '1px solid #D97706',
                                      bgcolor: '#FFFBEB',
                                      color: '#92400E',
                                      '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        lineHeight: 1.25,
                                        px: 0.75,
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                              {upcoming.startDate ? (() => {
                                const u = urgencyChip(upcoming.startDate);
                                if (!u) return null;
                                return (
                                  <Chip
                                    size="small"
                                    label={u.label}
                                    sx={{
                                      bgcolor: u.bg,
                                      color: u.fg,
                                      fontWeight: 800,
                                      fontSize: 9,
                                      height: 18,
                                      letterSpacing: 0.35,
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                );
                              })() : null}
                              <Chip
                                color={getColor(upcoming)}
                                size="small"
                                label={getLabel(upcoming)}
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  height: 18,
                                  letterSpacing: 0.35,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography
                            my={0.75}
                            variant="body2"
                            color="text.primary"
                            sx={{ fontSize: 11.5 }}>
                            <CalendarMonthIcon sx={iconStyle} />
                            {moment(upcoming.startDate).format('DD/MM/YYYY')} -{' '}
                            {moment(upcoming.endDate).format('DD/MM/YYYY')}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontSize: 11.5 }}>
                            <CelebrationIcon sx={iconStyle} /> {upcoming.eventType}
                            {upcoming.timeSlot && (
                              <>
                                {' '}
                                - <AccessTimeIcon sx={iconStyle} />
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.primary"
                                  sx={{ fontSize: 11.5 }}>
                                  {upcoming.timeSlot}
                                </Typography>
                              </>
                            )}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                    </ListItem>
                  </Box>
                </Fragment>
              );
              })
            ) : (
              <Box
                sx={{
                  py: 8,
                  textAlign: 'center',
                  color: 'text.secondary',
                }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                  {(() => {
                    const typeLabel = String(title || 'bookings').toLowerCase();
                    const selected = futureNoOfDaysOptions.find((o: any) => String(o.value) === String(noOfDays));
                    const rangeLabel = selected?.label ?? '';
                    // Example desired: "No tentative bookings for next 60 days"
                    return `No ${typeLabel} for ${String(rangeLabel).toLowerCase()}.`;
                  })()}
                </Typography>
                <Typography sx={{ mt: 1, fontSize: 13 }}>
                  Try increasing the time range to see more activity.
                </Typography>
              </Box>
            )}
          </List>
        </Scrollbar>
      </CardContent>
    </Card>
  );
};
export default UpComingBooking;
